import murmur from "murmurhash-js";

export type TranslateAPI = (texts: string[]) => Promise<string[]>;

export class DomTranslator {
  private observer: MutationObserver;

  private originalTextMap = new Map<Text | Attr | Element | string, string>();
  private translatedSet = new Set<Text | Attr | Element>();
  private translatedContentMap = new Map<string, string>();

  private readonly translateAPI: TranslateAPI;
  private readonly initSelections: () => void;
  private isStopped = false;
  private intervalId: number | null = null;
  private readonly targetLang: string = "en";
  private dataSequence = 0;
  private chunkSize = 50;

  private readonly ignoredTags = new Set([
    "SCRIPT",
    "STYLE",
    "NOSCRIPT",
    "META",
    "LINK",
    "SVG",
    "IMG",
    "VIDEO",
    "IFRAME",
    "I",
  ]);

  private readonly translatableAttrs = [
    "aria-label",
    "title",
    "alt",
    "placeholder",
    "src",
  ];

  private readonly ignoredClasses = new Set([
    "notranslate",
    "skiptranslate",
    "MathJax",
    "no-translate",
    "highlight-wrapper",
    "redactor-in",
  ]);

  private debounceTimer: number | null = null;
  private debounceDelay = 2000;

  constructor({
    translateAPI,
    initSelections,
  }: {
    translateAPI: TranslateAPI;
    initSelections: () => void;
  }) {
    this.translateAPI = translateAPI;
    this.initSelections = initSelections;
    this.targetLang = DomTranslator.getStoredLanguage();
    this.observer = new MutationObserver(this.handleMutations.bind(this));

    if (this.targetLang === "en") {
      return;
    }

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeOldValue: true,
    });

    this.startTitleTranslationObserver();
    this.queueTranslation();

    this.intervalId = window.setInterval(() => {
      if (!this.isStopped) {
        this.translateChangedOnly(true);
      }
    }, 2000);
  }

  static getStoredLanguage(): string {
    return localStorage.getItem("language") || "en";
  }

  static setStoredLanguage(lang: string): void {
    localStorage.setItem("language", lang);
  }

  public switchLanguage(newLang: string): void {
    if (newLang === this.targetLang) {
      return;
    }
    DomTranslator.setStoredLanguage(newLang);
    window.location.reload();
  }

  public stop(): void {
    this.isStopped = true;
    this.disconnect();
  }

  private startTitleTranslationObserver(): void {
    const titleObserver = new MutationObserver(() => {
      const currentTitle = document.title;
      if (
        currentTitle &&
        currentTitle.trim() &&
        currentTitle !== this.originalTextMap.get("<title>")
      ) {
        this.translateAPI([currentTitle]).then(([translatedTitle]) => {
          if (translatedTitle) {
            document.title = translatedTitle;
            this.originalTextMap.set("<title>", translatedTitle);
          }
        });
      }
    });

    const titleEl = document.querySelector("title");
    if (titleEl) {
      titleObserver.observe(titleEl, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
  }

  private hasIgnoredClass(el: Element | null): boolean {
    return (
      !!el?.classList &&
      Array.from(el.classList).some((cls) => this.ignoredClasses.has(cls))
    );
  }

  public async forceTranslate(): Promise<void> {
    if (this.isStopped) {
      return;
    }
    await this.translateChangedOnly(false);
  }

  private isInIgnoredTree(el: Element | null): boolean {
    while (el) {
      if (this.hasIgnoredClass(el)) {
        return true;
      }
      // eslint-disable-next-line no-param-reassign
      el = el.parentElement;
    }
    return false;
  }

  private isVisibleTextNode(node: Text): boolean {
    const parent = node.parentElement;
    return (
      !!parent &&
      !this.ignoredTags.has(parent.tagName) &&
      !this.isInIgnoredTree(parent) &&
      !parent.hasAttribute("data-translated") &&
      node.textContent?.trim() !== ""
    );
  }

  private collectTextNodes(): Text[] {
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
    );
    const nodes: Text[] = [];
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      if (this.isVisibleTextNode(node)) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  private collectAttributes(): Attr[] {
    const result: Attr[] = [];
    document.querySelectorAll("*").forEach((el) => {
      if (
        this.ignoredTags.has(el.tagName) ||
        this.isInIgnoredTree(el) ||
        el.hasAttribute("data-translated")
      ) {
        return;
      }

      this.translatableAttrs.forEach((attr) => {
        const attrNode = el.getAttributeNode(attr);
        if (attrNode && attrNode.value.trim()) {
          result.push(attrNode);
        }
      });

      if (
        el instanceof HTMLInputElement &&
        el.classList.contains("translatable") &&
        el.value.trim()
      ) {
        const valueAttr = el.getAttributeNode("value");
        if (valueAttr) {
          result.push(valueAttr);
        }
      }
    });
    return result;
  }

  private collectSentenceWrappers(): Element[] {
    return Array.from(
      document.querySelectorAll(".highlighting-sentence-wrapper"),
    ).filter((el) => !this.isInIgnoredTree(el) && !this.translatedSet.has(el));
  }

  // eslint-disable-next-line class-methods-use-this
  private splitTranslatedSentence(wrapper: HTMLElement, text: string): void {
    wrapper.innerHTML = "";
    const words = text.split(/\s+/).filter(Boolean);
    words.forEach((word) => {
      const span = document.createElement("span");
      span.setAttribute("data-translated", "true");
      span.setAttribute("data-hash", `${murmur.murmur3(`${word} `)}`);
      span.setAttribute("data-sequence", String((this.dataSequence += 1)));
      span.className = "highlight-wrapper";
      span.textContent = `${word} `;
      wrapper.appendChild(span);
      wrapper.appendChild(document.createTextNode(" "));
    });
    this.initSelections();
  }

  private async translateChangedOnly(useParallelism = true): Promise<void> {
    if (this.isStopped) {
      return;
    }

    const toTranslate: {
      node: Text | Attr | Element | string;
      text: string;
    }[] = [];
    const handled = new Set<Node>();

    // eslint-disable-next-line no-restricted-syntax
    for (const wrapper of this.collectSentenceWrappers()) {
      const sentenceText = wrapper.textContent?.trim() || "";
      const original = this.originalTextMap.get(wrapper);
      if (!sentenceText || sentenceText === original) {
        // eslint-disable-next-line no-continue
        continue;
      }

      this.originalTextMap.set(wrapper, sentenceText);
      toTranslate.push({ node: wrapper, text: sentenceText });

      wrapper
        .querySelectorAll(".highlight-wrapper")
        .forEach((el) => handled.add(el));
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const node of this.collectTextNodes()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (handled.has(node.parentElement!)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const text = node.textContent || "";
      const original = this.originalTextMap.get(node);
      if (text === original || this.translatedSet.has(node)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      this.originalTextMap.set(node, text);
      toTranslate.push({ node, text });
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const attr of this.collectAttributes()) {
      const { value } = attr;
      const original = this.originalTextMap.get(attr);
      if (value === original || this.translatedSet.has(attr)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      this.originalTextMap.set(attr, value);
      toTranslate.push({ node: attr, text: value });
    }

    if (toTranslate.length === 0) {
      return;
    }

    const texts = toTranslate.map((t) => t.text);

    const handleNodes = (translated: string[], i = 0): void => {
      const chunkToTranslate = toTranslate.slice(i, i + this.chunkSize);

      chunkToTranslate.forEach(({ node }, idx) => {
        const newText = translated[idx];

        if (node instanceof HTMLElement) {
          this.splitTranslatedSentence(node, newText);
          node.setAttribute("data-translated", "true");
          this.translatedSet.add(node);
        } else if (node instanceof Text) {
          node.textContent = newText;
          const parent = node.parentElement;
          if (parent) {
            parent.setAttribute("data-translated", "true");
          }
          this.translatedSet.add(node);
        } else if (node instanceof Attr) {
          node.value = newText;
          const el = node.ownerElement;
          if (
            node.name === "value" &&
            el instanceof HTMLInputElement &&
            el.classList.contains("translatable")
          ) {
            el.value = newText;
          }
          this.translatedSet.add(node);
        }
      });
    };

    if (useParallelism) {
      this.chunkSize = 50;
      for (let i = 0; i < texts.length; i += this.chunkSize) {
        const chunk = texts.slice(i, i + this.chunkSize);
        this.translateAPI(chunk).then((translated) => {
          handleNodes(translated, i);
        });
      }
    } else {
      this.chunkSize = 10000;
      const translated = await this.translateAPI(texts);
      handleNodes(translated);
      return Promise.resolve();
    }
  }

  private queueTranslation(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(() => {
      this.translateChangedOnly(true);
    }, this.debounceDelay);
  }

  private handleMutations(mutations: MutationRecord[]): void {
    if (this.isStopped) {
      return;
    }

    let shouldTranslate = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const mutation of mutations) {
      const target = mutation.target as Element;

      if (target?.hasAttribute?.("data-translated")) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (mutation.type === "childList" || mutation.type === "characterData") {
        shouldTranslate = true;
        // eslint-disable-next-line no-continue
        continue;
      }

      if (mutation.type === "attributes") {
        const attrName = mutation.attributeName;
        if (attrName === "class" || attrName?.startsWith("data-")) {
          const id = target.getAttribute("data-translate-id");
          if (id) {
            const beforeSelector = `[data-translate-id="${id}"]::before`;
            const afterSelector = `[data-translate-id="${id}"]::after`;
            this.translatedContentMap.delete(beforeSelector);
            this.translatedContentMap.delete(afterSelector);
          }
          shouldTranslate = true;
        }
      }
    }

    if (shouldTranslate) {
      this.queueTranslation();
    }
  }

  public disconnect(): void {
    this.observer.disconnect();
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
